interface Translations {
  [key: string]: string;
}

const translationCache = new Map<string, Translations>();

export const LANGUAGE_CONFIG: Record<
  string,
  { normalized: string; filename: string }
> = {
  en: { normalized: "en-US", filename: "en" },
  "en-US": { normalized: "en-US", filename: "en" },
  "en-GB": { normalized: "en-GB", filename: "en-gb" },
  fr: { normalized: "fr-CA", filename: "fr" },
  "fr-CA": { normalized: "fr-CA", filename: "fr" },
  hi: { normalized: "hi-IN", filename: "hi" },
  "hi-IN": { normalized: "hi-IN", filename: "hi" },
  de: { normalized: "de", filename: "de" },
  es: { normalized: "es-ES", filename: "es" },
  "es-ES": { normalized: "es-ES", filename: "es" },
  da: { normalized: "da", filename: "da" },
  fi: { normalized: "fi", filename: "fi" },
  nl: { normalized: "nl", filename: "nl" },
  no: { normalized: "no", filename: "no" },
  pt: { normalized: "pt", filename: "pt" },
  "pt-BR": { normalized: "pt-BR", filename: "pt-br" },
  sv: { normalized: "sv", filename: "sv" },
  ar: { normalized: "ar", filename: "ar" },
  pl: { normalized: "pl", filename: "pl" },
  it: { normalized: "it", filename: "it" },
  ja: { normalized: "ja", filename: "ja" },
  ko: { normalized: "ko", filename: "ko" },
  zh: { normalized: "zh-CN", filename: "zh" },
  "zh-CN": { normalized: "zh-CN", filename: "zh" },
  "zh-TW": { normalized: "zh-TW", filename: "zh" },
  ru: { normalized: "ru", filename: "ru" },
  tr: { normalized: "tr", filename: "tr" },
  cs: { normalized: "cs", filename: "cs" },
  hu: { normalized: "hu", filename: "hu" },
  ro: { normalized: "ro", filename: "ro" },
  uk: { normalized: "uk", filename: "uk" },
  th: { normalized: "th", filename: "th" },
  vi: { normalized: "vi", filename: "vi" },
  id: { normalized: "id", filename: "id" },
};

export const DEFAULT_LANGUAGE = "en-US";
export const SUPPORTED_LANGUAGES = [
  "en-US",
  "en-GB",
  "fr-CA",
  "hi-IN",
  "de",
  "es-ES",
  "da",
  "fi",
  "nl",
  "no",
  "pt",
  "pt-BR",
  "sv",
  "ar",
  "pl",
  "it",
  "ja",
  "ko",
  "zh-CN",
  "zh-TW",
  "ru",
  "tr",
  "cs",
  "hu",
  "ro",
  "uk",
  "th",
  "vi",
  "id",
];

// Language display configuration for UI components
export const AVAILABLE_LANGUAGES = {
  "en-US": { localName: "English", name: "english", code: "en-US" },
  "en-GB": { localName: "English (UK)", name: "english", code: "en-GB" },
  en: { localName: "English", name: "english", code: "en" },
  "fr-CA": { localName: "Français", name: "french", code: "fr-CA" },
  fr: { localName: "Français", name: "french", code: "fr" },
  "hi-IN": { localName: "हिंदी", name: "hindi", code: "hi-IN" },
  hi: { localName: "हिंदी", name: "hindi", code: "hi" },
  de: { localName: "Deutsch", name: "german", code: "de" },
  "es-ES": { localName: "Española", name: "spanish", code: "es-ES" },
  es: { localName: "Española", name: "spanish", code: "es" },
  da: { localName: "Dansk", name: "danish", code: "da" },
  fi: { localName: "Suomi", name: "finnish", code: "fi" },
  nl: { localName: "Nederlands", name: "dutch", code: "nl" },
  no: { localName: "Norsk", name: "norwegian", code: "no" },
  pt: { localName: "Português", name: "portuguese", code: "pt" },
  "pt-BR": {
    localName: "Português (Brasil)",
    name: "portuguese",
    code: "pt-BR",
  },
  sv: { localName: "Svenska", name: "swedish", code: "sv" },
  ar: { localName: "العربية", name: "arabic", code: "ar" },
  pl: { localName: "Polski", name: "polish", code: "pl" },
  it: { localName: "Italiano", name: "italian", code: "it" },
  ja: { localName: "日本語", name: "japanese", code: "ja" },
  ko: { localName: "한국어", name: "korean", code: "ko" },
  zh: { localName: "中文", name: "chinese", code: "zh" },
  "zh-CN": { localName: "简体中文", name: "chinese", code: "zh-CN" },
  "zh-TW": {
    localName: "繁體中文",
    name: "chinese",
    code: "zh-TW",
  },
  ru: { localName: "Русский", name: "russian", code: "ru" },
  tr: { localName: "Türkçe", name: "turkish", code: "tr" },
  cs: { localName: "Čeština", name: "czech", code: "cs" },
  hu: { localName: "Magyar", name: "hungarian", code: "hu" },
  ro: { localName: "Română", name: "romanian", code: "ro" },
  uk: { localName: "Українська", name: "ukrainian", code: "uk" },
  th: { localName: "ไทย", name: "thai", code: "th" },
  vi: { localName: "Tiếng Việt", name: "vietnamese", code: "vi" },
  id: { localName: "Bahasa Indonesia", name: "indonesian", code: "id" },
};

// Priority languages to preload (most commonly used)
const PRIORITY_LANGUAGES = ["en-US", "fr-CA"];

export const preloadTranslations = async (
  languages: string[] = PRIORITY_LANGUAGES
) => {
  const preloadPromises = languages.map(async (lang) => {
    if (translationCache.has(lang)) {
      return; // Already cached
    }
    try {
      const fileName = LANGUAGE_CONFIG[lang]?.filename || "en";
      if (fileName) {
        const translations = await import(`../locales/${fileName}.json`);
        translationCache.set(lang, translations);
      }
    } catch (error) {
      console.warn(`Failed to preload translations for ${lang}:`, error);
    }
  });
  await Promise.allSettled(preloadPromises);
};

// Preload on idle
export const preloadOnIdle = () => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => {
      preloadTranslations();
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      preloadTranslations();
    }, 2000);
  }
};

export const getTranslationFromCache = (
  language: string
): Translations | null => {
  return translationCache.get(language) || null;
};

export const setCachedTranslation = (
  language: string,
  translations: Translations
) => {
  translationCache.set(language, translations);
};

export function getShortLanguageCode(locale: string): string {
  if (!locale) return "";
  return locale; //locale.split("-")[0]; //don't need shortcode
}

// Utility function to check if a language is supported
export const isLanguageSupported = (
  language: string,
  supportedLanguages: string[] = SUPPORTED_LANGUAGES
): boolean => {
  if (!language) return false;
  return supportedLanguages.includes(language);
};

// Get the best matching language from a list of preferences
export const getBestMatchingLanguage = (
  preferredLanguages: string[],
  supportedLanguages: string[] = SUPPORTED_LANGUAGES
): string => {
  for (const lang of preferredLanguages) {
    if (isLanguageSupported(lang, supportedLanguages)) {
      return lang;
    }
  }
  return DEFAULT_LANGUAGE;
};

// Type for language display object
export type LanguageDisplayInfo = {
  localName: string;
  name: string;
  code: string;
};

// Get available languages filtered by supported languages and language config
export const getAvailableLanguages = (
  supportedLanguages: string[]
): Record<string, LanguageDisplayInfo> => {
  const filteredLanguages: Record<string, LanguageDisplayInfo> = {};

  supportedLanguages.forEach((langCode) => {
    if (
      LANGUAGE_CONFIG[langCode] &&
      AVAILABLE_LANGUAGES[langCode as keyof typeof AVAILABLE_LANGUAGES]
    ) {
      filteredLanguages[langCode] =
        AVAILABLE_LANGUAGES[langCode as keyof typeof AVAILABLE_LANGUAGES];
    }
  });

  return filteredLanguages;
};
